'use client';

import { BiRightArrowAlt } from 'react-icons/bi';
import React, { ReactElement } from 'react';
import { useModal } from '@/components/ModalContext/ModalContext';

export type LouiseButtonColorType =
  | 'blue'
  | 'white'
  | 'gray'
  | 'gray-light'
  | 'red'
  | 'green'
  | 'purple'
  | 'indigo'
  | 'transparent'
  | 'transparent-text-light';
export type CtaTargetType =
  | 'register'
  | 'home'
  | 'login'
  | 'contact'
  | 'plans'
  | 'partner-register'
  | 'roi-calculator'
  | 'discover-human-clone-link'
  | 'discover-chatbot-type'
  | 'revenue-calculator'
  | 'discover-chatbot-text'
  | 'discover-chatbot-ai'
  | 'discover-videobot'
  | 'external'
  | 'other';

type props = {
  color: LouiseButtonColorType;
  cta: string;
  ctaTarget: CtaTargetType;
  gradiant?: boolean;
  icon?: ReactElement;
  href?: string;
  animation?: boolean;
  modalProps?: any;
  onClick?: () => void;
  locale: string;
  fullWith?: boolean;
};

export default function LouiseButton({
  color,
  cta,
  gradiant,
  ctaTarget,
  icon,
  href,
  animation,
  modalProps = {},
  onClick,
  locale,
  fullWith,
}: props) {
  let textColor, gradiantColor, bgColor;
  let needRedirect = true;
  const { openModal } = useModal();
  switch (color) {
    case 'blue':
      textColor = 'tw-text-white';
      gradiantColor = 'tw-from-blue-600 tw-to-purple-600';
      bgColor = `tw-bg-blue-600`;
      break;
    case 'white':
      textColor = 'tw-text-blue-600';
      gradiantColor = 'tw-from-gray-300 tw-to-white';
      bgColor = `tw-bg-white`;
      break;
    case 'gray':
      textColor = 'tw-text-white tw-border-1 tw-border-gray-600';
      gradiantColor = 'tw-from-gray-600 tw-to-transparent';
      bgColor = `tw-bg-gray-600`;
      break;
    case 'gray-light':
      textColor = 'tw-text-gray-900 tw-border-[1px] tw-border-gray-300';
      gradiantColor = 'tw-from-gray-500 tw-to-transparent';
      bgColor = `tw-bg-gray-100`;
      break;
    case 'green':
      textColor = 'tw-text-white';
      gradiantColor = 'tw-from-green-500 tw-to-emerald-600';
      bgColor = `tw-bg-green-600`;
      break;
    case 'purple':
      textColor = 'tw-text-white';
      gradiantColor = 'tw-from-purple-500 tw-to-pink-600';
      bgColor = `tw-bg-purple-600`;
      break;
    case 'red':
      textColor = 'tw-text-white';
      gradiantColor = 'tw-from-red-400 tw-to-red-700';
      bgColor = `tw-bg-red-600`;
      break;
    case 'indigo':
      textColor = 'tw-text-white';
      gradiantColor = 'tw-from-indigo-400 tw-to-indigo-700';
      bgColor = `tw-bg-indigo-600`;
      break;
    case 'transparent':
      textColor = 'tw-text-blue-600';
      gradiantColor = '';
      bgColor = ``;
      break;
    case 'transparent-text-light':
      textColor = 'tw-text-white/80';
      gradiantColor = '';
      bgColor = ``;
      break;
    default:
      textColor = 'tw-text-white';
      gradiantColor = 'tw-from-blue-600 tw-to-purple-600';
      bgColor = `tw-bg-${color}-600`;
      break;
  }

  const getDestination = () => {
    switch (ctaTarget) {
      case 'register':
      case 'login':
        return `${process.env.NEXT_PUBLIC_DASHBOARD_BASE_URL}/${locale}/${ctaTarget}`;
      case 'home':
        return `/${locale}/`;
      case 'contact':
        return `/${locale}/contact-us`;
      case 'plans':
        return `/${locale}/plans`;
      case 'partner-register':
        return `${process.env.NEXT_PUBLIC_DASHBOARD_BASE_URL}/${locale}/partner/business/register`;
      case 'roi-calculator':
      case 'revenue-calculator':
      case 'discover-chatbot-ai':
      case 'discover-chatbot-text':
      case 'discover-videobot':
        needRedirect = false;
        return '/#';
      case 'other':
        return `/${locale}/${href}`;
      case 'external':
        return href;
      default:
        return `${process.env.NEXT_PUBLIC_DASHBOARD_BASE_URL}/${locale}/register`;
    }
  };

  const destination = getDestination();

  const handleClick = (e: React.MouseEvent) => {
    if (onClick) {
      onClick();
      return;
    }

    switch (ctaTarget) {
      case 'roi-calculator':
        openModal('ROI_CALCULATOR', {
          ...modalProps,
          source: cta,
        });
        e.preventDefault();
        break;
      case 'revenue-calculator':
        openModal('REVENUE_CALCULATOR', {
          ...modalProps,
          source: cta,
        });
        e.preventDefault();
        break;
      case 'discover-chatbot-text':
        openModal('DISCOVER_CHATBOT_TEXT', {
          ...modalProps,
          source: cta,
        });
        e.preventDefault();
        break;
      case 'discover-chatbot-ai':
        openModal('DISCOVER_CHATBOT_AI', {
          ...modalProps,
          source: cta,
        });
        e.preventDefault();
        break;
      case 'discover-videobot':
        openModal('DISCOVER_VIDEOBOT', {
          ...modalProps,
          source: cta,
        });
        e.preventDefault();
        break;
      default:
        break;
    }
  };

  const animationCss = animation ? 'tw-transition-all tw-duration-300 hover:tw-scale-105' : '';
  const outline = `${textColor} tw-rounded-xl tw-font-semibold tw-font-['Lexend'] tw-px-6 tw-py-3 tw-items-center tw-justify-center tw-inline-flex tw-gap-2 ${animationCss} `;
  let commonStyle = color === 'transparent' ? outline : outline + `tw-shadow-lg hover:tw-shadow-xl`;
  if (fullWith) {
    commonStyle = commonStyle + ' tw-w-full';
  }
  const style = gradiant ? `${commonStyle} tw-bg-gradient-to-br ${gradiantColor}` : `${commonStyle} ${bgColor}`;
  return (
    <div>
      {needRedirect ? (
        <a href={destination} className={style} aria-label={cta} target={ctaTarget === 'external' ? '_blank' : '_self'}>
          {icon}
          <div className="tw-px-2">{cta}</div>
          <BiRightArrowAlt className="tw-w-4 tw-h-4" />
        </a>
      ) : (
        <button className={style} onClick={handleClick}>
          {icon}
          <div className="tw-px-2">{cta}</div>
          <BiRightArrowAlt className="tw-w-4 tw-h-4" />
        </button>
      )}
    </div>
  );
}
